import { useCallback, useEffect, useMemo, useState } from "react";
import { getDetalle, getProductosRelacionados, notificarCotizacion } from "../../../services/backend/cotizacionService";
import { toObraOption, toRelatedItem } from "../../../services/transforms/cartDataTransforms";

const useRelatedProducts = ({ cotizacionId, cartItems, cartResumen, addToCart, toastRef }) => {
  const [selectedObraId, setSelectedObraId] = useState(null);
  const [relatedItems, setRelatedItems] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState("");
  const [relatedAddStatus, setRelatedAddStatus] = useState("idle");

  useEffect(() => {
    if (cartResumen?.proyectos) {
      const obras = (Array.isArray(cartResumen.proyectos) ? cartResumen.proyectos : [])
        .filter((obra) => obra?.activo !== false)
        .map(toObraOption)
        .filter((obra) => obra.id && obra.nombre);
      setSelectedObraId((prev) => (obras.some((o) => o.id === prev) ? prev : (obras[0]?.id || "")));
    } else {
      setSelectedObraId("");
    }
  }, [cartResumen]);

  useEffect(() => {
    if (!cotizacionId || selectedObraId || cartItems.length < 4) return;
    let isCurrent = true;
    getDetalle(false, false, cotizacionId, "RETAIL")
      .then((detalle) => {
        if (!isCurrent) return;
        const proyectos = Array.isArray(detalle?.proyectos) ? detalle.proyectos : [];
        const obras = proyectos
          .filter((obra) => obra?.activo !== false)
          .map(toObraOption)
          .filter((obra) => obra.id && obra.nombre);
        if (obras.length) {
          setSelectedObraId(obras[0].id);
        }
      })
      .catch(() => {});
    return () => { isCurrent = false; };
  }, [cotizacionId, selectedObraId, cartItems.length]);

  useEffect(() => {
    if (!cotizacionId || !selectedObraId || cartItems.length < 4) {
      setRelatedItems([]);
      setRelatedError("");
      return;
    }
    let isCurrent = true;
    setRelatedLoading(true);
    setRelatedError("");
    notificarCotizacion(cotizacionId).catch(() => {});
    getProductosRelacionados(cotizacionId, selectedObraId)
      .then((resp) => {
        if (!isCurrent) return;
        const source = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.data)
            ? resp.data
            : Array.isArray(resp?.relacionados)
              ? resp.relacionados
              : [];
        const uniqueByCode = new Map();
        source.forEach((product) => {
          const related = toRelatedItem(product);
          if (!related.code || uniqueByCode.has(related.code)) return;
          uniqueByCode.set(related.code, related);
        });
        setRelatedItems(Array.from(uniqueByCode.values()));
      })
      .catch((error) => {
        if (!isCurrent) return;
        setRelatedItems([]);
        setRelatedError(error?.message || "No fue posible cargar productos relacionados.");
      })
      .finally(() => {
        if (isCurrent) setRelatedLoading(false);
      });
    return () => { isCurrent = false; };
  }, [cotizacionId, selectedObraId, cartItems.length]);

  const filteredRelatedItems = useMemo(() => {
    const inCartCodes = new Set(cartItems.map((item) => String(item.code || "")));
    return relatedItems.filter((item) => !inCartCodes.has(String(item.code || "")));
  }, [relatedItems, cartItems]);

  const handleAddRelatedProduct = useCallback(async (item) => {
    if (!cotizacionId) return;
    setRelatedAddStatus("loading");
    try {
      const result = await addToCart({ code: item.code, name: item.name, price: item.price, image: item.image }, 1);
      if (result.success) {
        toastRef.current?.show({
          severity: "success",
          summary: "Producto agregado",
          detail: `${item.name} agregado al carro`,
          life: 2500,
        });
        setRelatedAddStatus("success");
      } else {
        throw new Error(result.error || "Error al agregar producto");
      }
    } catch (error) {
      setRelatedAddStatus("error");
      toastRef.current?.show({
        severity: "warn",
        icon: "pi pi-exclamation-triangle",
        summary: "No se pudo agregar producto relacionado al carro",
        detail: error?.message || "Ocurrió un error al agregar el producto.",
        life: 3000,
      });
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 700));
      setRelatedAddStatus("idle");
    }
  }, [cotizacionId, addToCart, toastRef]);

  return {
    filteredRelatedItems,
    relatedLoading,
    relatedError,
    relatedAddStatus,
    handleAddRelatedProduct,
  };
};

export default useRelatedProducts;
